/**
 * How far beyond the viewport a page still gets a live Fabric EDITOR, in CSS pixels.
 *
 * Wide enough that a stroke started right after a fast scroll usually lands on a page
 * that is already mounted. Mounting one on demand at the contact works, but it is a
 * synchronous editor build under the pen, which reads as lag.
 */
export const BAND_MARGIN_PX = 900;

/**
 * How far beyond the viewport a page gets its read-only SVG OVERLAY, in CSS pixels.
 *
 * This MUST be much larger than the editor band. The two used to share one margin,
 * so a page leaving the editor band left the overlay band in the same frame: it lost
 * its editor and was skipped by the overlay pass, and its ink simply disappeared
 * while scrolling. The overlay is the fallback for every page without an editor, so
 * its band has to contain the editor band with room to spare.
 *
 * It is not unbounded, because that is what made an 80-page note unusable: the
 * overlay pass built every stroke in the document in one synchronous go.
 */
export const OVERLAY_BAND_MARGIN_PX = 2400;

/**
 * Below this page count the reader mounts every editor and skips the band
 * machinery entirely.
 *
 * Virtualization cannot pay on a document that fits in the band: every page is
 * mounted either way, so the rect reads, the trailing reconcile timer and the extra
 * overlay pass are pure cost. Measured on 2026-09-19, a 1-page note entered write
 * mode 13 to 27 percent SLOWER with the band always on, while a 20-page note was 67
 * percent faster. The threshold keeps the win and drops the loss.
 */
export const VIRTUALIZE_MIN_PAGES = 4;

/**
 * Whether a page element sits inside the viewport, or within `margin` of it.
 *
 * This is the one visibility test the reader virtualizes by. `PdfViewer` already
 * used this rule to decide which pages to rasterize and which to draw an overlay
 * for; the live Fabric editors now use the same band, so a page cannot be
 * rasterized by one rule and given an editor by another.
 */
export function inViewportBand(
  root: HTMLElement,
  el: HTMLElement,
  margin = BAND_MARGIN_PX,
): boolean {
  const box = root.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return r.bottom > box.top - margin && r.top < box.bottom + margin;
}

/**
 * The page numbers whose elements are in the band.
 *
 * `pin` is a page that must stay in the set whatever the geometry says — the page
 * under an active pen. Tearing that editor down mid-stroke would drop the stroke.
 */
export function pagesInBand(
  root: HTMLElement | null,
  els: Map<number, HTMLElement>,
  pin?: number | null,
  margin = BAND_MARGIN_PX,
): Set<number> {
  const out = new Set<number>();
  if (root) {
    for (const [p, el] of els) if (inViewportBand(root, el, margin)) out.add(p);
  }
  if (pin != null) out.add(pin);
  return out;
}
